<template>
  <main
    class="school-enrollments-page"
    role="main"
    aria-labelledby="page-title"
  >
    <div class="page-header">
      <h1
        id="page-title"
        class="h1 pb-1 pt-2"
      >
        Schulanmeldungen
      </h1>
    </div>

    <div class="card">
      <!-- Filterleiste (Auto-Reload on change) -->
      <div
        class="filters mb-0"
        aria-label="Filter"
      >
        <div class="field">
          <label for="se-search">Suche</label>
          <input
            id="se-search"
            v-model="search"
            class="input"
            type="text"
            placeholder="Vorname oder Nachname"
            autocomplete="off"
            :disabled="loading"
            @change="reload()"
          >
        </div>

        <div class="field">
          <label for="se-school-year">Schuljahr</label>
          <select
            id="se-school-year"
            v-model="filterSchoolYear"
            class="input"
            :disabled="loading"
            @change="reload()"
          >
            <option value="">
              Alle
            </option>
            <option
              v-for="sy in schoolYearFacetOptions"
              :key="sy.label"
              :value="sy.label"
            >
              {{ sy.label }} ({{ sy.count }})
            </option>
          </select>
        </div>

        <div class="field">
          <label for="se-class-level">Klasse</label>
          <select
            id="se-class-level"
            v-model="filterClassLevel"
            class="input"
            :disabled="loading"
            @change="reload()"
          >
            <option value="">
              Alle
            </option>
            <option
              v-for="cl in classLevelFacetOptions"
              :key="cl.label"
              :value="cl.label"
            >
              {{ cl.label }} ({{ cl.count }})
            </option>
          </select>
        </div>

        <div class="field">
          <label for="se-status-filter">Status</label>
          <select
            id="se-status-filter"
            v-model="statusFilter"
            class="input"
            :disabled="loading"
            @change="reload()"
          >
            <option value="">
              Alle
            </option>
            <option value="unread">
              Ungelesen
            </option>
            <option value="notExported">
              Nicht exportiert
            </option>
          </select>
        </div>

        <div class="field filters-actions">
          <button
            class="btn"
            type="button"
            :disabled="loading || (!search && !filterSchoolYear && !filterClassLevel && !statusFilter)"
            @click="resetAllFilters()"
          >
            Reset
          </button>
        </div>
      </div>

      <!-- Bulk Actions (zentral unter Filter, oberhalb Tabelle) -->
      <div
        class="bulk-actions"
        aria-label="Bulk-Aktionen"
      >
        <button
          class="btn"
          type="button"
          :disabled="loading || selectedIds.length === 0"
          aria-label="Massen-PDF exportieren"
          @click="bulkPdf()"
        >
          <i
            class="pi pi-file-pdf"
            aria-hidden="true"
            style="margin-right:8px;"
          />
          PDF
        </button>

        <button
          class="btn"
          type="button"
          :disabled="loading || selectedIds.length === 0"
          aria-label="Massen-CSV exportieren"
          @click="bulkCsv()"
        >
          <i
            class="pi pi-download"
            aria-hidden="true"
            style="margin-right:8px;"
          />
          CSV
        </button>

        <span
          class="muted"
          style="margin-left:auto"
        >
          Ausgewählt: {{ selectedIds.length }}
        </span>
      </div>

      <!-- Tabelle -->
      <div
        class="table-wrap"
        data-testid="school-enrollments-table"
      >
        <table
          class="table"
          role="table"
          aria-label="Liste Schulanmeldungen"
        >
          <thead>
            <tr class="bg-gray-100">
              <th style="width:36px;">
                <input
                  type="checkbox"
                  :checked="allChecked"
                  aria-label="Alle auswählen"
                  @change="toggleAll"
                >
              </th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th class="text-center">
                Geburtsdatum
              </th>
              <th class="text-center">
                Schuljahr
              </th>
              <th class="text-center">
                Klasse
              </th>
              <th
                class="text-center"
                style="width:180px;"
              >
                Anmeldedatum
              </th>
              <th
                class="text-right"
                style="width:120px;"
              >
                Aktion
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in items"
              :key="item.id"
              :class="item.readAt ? '' : 'row-unread'"
            >
              <td class="white-space-nowrap">
                <input
                  v-model="selectedIds"
                  type="checkbox"
                  :value="item.id"
                  :aria-label="`Auswählen: ${item.childFirstName} ${item.childLastName}`"
                >

                <span
                  v-if="item.exportedAt"
                  class="status-icon"
                  title="exportiert"
                >
                  <i
                    class="pi pi-download status-on"
                    style="font-size: 10px;"
                    aria-hidden="true"
                  />
                </span>
              </td>
              <td>{{ item.childFirstName }}</td>
              <td>{{ item.childLastName }}</td>
              <td class="text-center">
                {{ formatDate(item.childBirthDate) }}
              </td>
              <td class="text-center">
                {{ item.schoolYear }}
              </td>
              <td class="text-center">
                {{ item.classLevel || '—' }}
              </td>
              <td class="text-center">
                {{ formatDateTime(item.createdAt) }}
              </td>
              <td class="text-right">
                <div class="row-actions">
                  <button
                    class="icon-btn hover:bg-green-100"
                    type="button"
                    @click="openDetails(item)"
                  >
                    <i
                      class="pi pi-eye"
                      aria-hidden="true"
                    />
                  </button>
                  <button
                    class="icon-btn btn-danger hover:bg-red-100"
                    type="button"
                    @click="deleteItem(item)"
                  >
                    <i
                      class="pi pi-trash"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!loading && items.length === 0">
              <td
                colspan="9"
                class="muted"
                style="text-align:center;padding:16px;"
              >
                Keine Einträge gefunden.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination (Aufteilung wie in Groups.vue) -->
      <div class="mt-12 pagination-row">
        <span
          class="muted"
          style="margin-right:auto"
        >Gesamt: {{ total }}</span>

        <label
          class="muted"
          for="se-limit"
        >pro Seite:</label>
        <select
          id="se-limit"
          v-model.number="limit"
          class="input"
          style="width:auto;"
          :disabled="loading"
          @change="goPage(1)"
        >
          <option :value="10">
            10
          </option>
          <option :value="20">
            20
          </option>
          <option :value="50">
            50
          </option>
        </select>

        <button
          class="btn"
          :disabled="page<=1 || loading"
          @click="goPage(page-1)"
        >
          Zurück
        </button>
        <span class="muted">Seite {{ page }} / {{ maxPage }}</span>
        <button
          class="btn"
          :disabled="page>=maxPage || loading"
          @click="goPage(page+1)"
        >
          Weiter
        </button>
      </div>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>
    </div>

    <!-- Details Dialog -->
    <div
      v-if="detailsOpen"
      class="details-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Anmeldedetails"
    >
      <div class="details-card card">
        <header style="display:flex;justify-content:space-between;align-items:center;">
          <h2 class="h2 mb-3">
            Details
          </h2>
          <div style="display: inline-flex; gap: 4px;">
            <button
              class="btn hover:bg-blue-100"
              type="button"
              @click="printDetails()"
            >
              <i
                class="pi pi-file-pdf"
                aria-hidden="true"
              />
            </button>
            <button
              class="btn hover:bg-gray-100"
              type="button"
              @click="detailsOpen=false"
            >
              Schließen
            </button>
          </div>
        </header>
        <div class="details-body">
          <div class="grid2">
            <div><strong>Vorname:</strong> {{ details?.childFirstName }}</div>
            <div><strong>Nachname:</strong> {{ details?.childLastName }}</div>
            <div><strong>Geburtsdatum:</strong> {{ formatDate(details?.childBirthDate) }}</div>
            <div><strong>Anmeldung für die:</strong> {{ schoolTypeLabel }}</div>
            <div><strong>Schuljahr:</strong> {{ details?.schoolYear }}</div>
            <div><strong>Klasse:</strong> {{ details?.data?.classLevel }}</div>
          </div>
          <hr>

          <div>
            <h3 class="h3 underline mb-3">
              Weitere Angaben
            </h3>
            <p
              v-if="detailsRows.length === 0"
              class="muted"
            >
              Keine weiteren Angaben.
            </p>
            <ul
              v-else
              class="kv-list"
              aria-label="Weitere Angaben (übersichtlich)"
            >
              <li
                v-for="(r, idx) in detailsRows"
                :key="idx"
                class="kv-item"
                :class="r.type === 'section' ? 'kv-section' : ''"
                :style="{ paddingLeft: `${r.level * 18}px` }"
              >
                <template v-if="r.type === 'section'">
                  <strong>{{ r.label }}</strong>
                </template>
                <template v-else>
                  <span class="kv-label">{{ r.label }}:</span>
                  <span class="kv-value">{{ r.value }}</span>
                </template>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import api from '@/services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const items = ref([]);
const total = ref(0);
const page = ref(1);
const limit = ref(20);
const loading = ref(false);
const error = ref('');
const search = ref('');
const filterSchoolYear = ref('');
const filterClassLevel = ref('');
const statusFilter = ref(''); // '', 'unread', 'notPrinted', 'notExported'
const schoolYearFacetOptions = ref([]); // {label, count}
const classLevelFacetOptions = ref([]); // {label, count}

const selectedIds = ref([]);
const allChecked = computed(() => items.value.length > 0 && selectedIds.value.length === items.value.length);

const offset = computed(() => (page.value - 1) * limit.value);
const maxPage = computed(() => Math.max(1, Math.ceil(total.value / limit.value)));

const schoolTypeLabel = computed(() => {
  const unwrapped = unwrapEnrollmentData(details.value);
  const raw = String(unwrapped?.schoolType ?? '').trim();

  if (!raw) return '—';
  if (raw === 'grundschule') return 'Grundschule';
  if (raw === 'sek1') return 'Sekundarstufe I';
  return raw;
});

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return String(iso);
  }
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}


async function fetchFacetOptions() {
  // Aggregiert alle vorhandenen Schuljahre/Klassen über paginierte Abfragen
  const limitLocal = 200;
  let offsetLocal = 0;
  const yearMap = new Map();
  const classMap = new Map();
  let totalLocal = 0;
  try {
    do {
      const {data} = await api.get('/admin/school-enrollments', {
        params: {limit: limitLocal, offset: offsetLocal}
      });
      const itemsLocal = data?.items || [];
      totalLocal = data?.total ?? itemsLocal.length;

      for (const it of itemsLocal) {
        const sy = (it.schoolYear ?? '').trim();
        const cl = (it.classLevel ?? '').trim();

        if (sy) yearMap.set(sy, (yearMap.get(sy) || 0) + 1);
        if (cl) classMap.set(cl, (classMap.get(cl) || 0) + 1);
      }

      offsetLocal += limitLocal;
    } while (offsetLocal < totalLocal);

    schoolYearFacetOptions.value = Array
        .from(yearMap.entries())
        .map(([label, count]) => ({label, count}))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), 'de'));

    classLevelFacetOptions.value = Array
        .from(classMap.entries())
        .map(([label, count]) => ({label, count}))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), 'de'));
  } catch (e) {
    schoolYearFacetOptions.value = [];
    classLevelFacetOptions.value = [];
  }
}

function resetAllFilters() {
  search.value = '';
  filterSchoolYear.value = '';
  filterClassLevel.value = '';
  statusFilter.value = '';
  reload();
}

async function markRead(id) {
  try {
    await api.post(`/admin/school-enrollments/${id}/read`);
  } catch (e) {
    // bewusst still: Status-Markierung ist nicht kritisch für die Kernfunktion
  }
}

async function markPrinted(id) {
  try {
    await api.post(`/admin/school-enrollments/${id}/printed`);
  } catch (e) {
    // ignored
  }
}

async function markExported(id) {
  try {
    await api.post(`/admin/school-enrollments/${id}/exported`);
  } catch (e) {
    // ignored
  }
}

async function fetchList() {
  loading.value = true;
  error.value = '';
  try {
    const params = {
      limit: limit.value,
      offset: offset.value,
      search: search.value || undefined,
      schoolYear: filterSchoolYear.value || undefined,
      classLevel: filterClassLevel.value || undefined,
      unread: statusFilter.value === 'unread' ? 1 : undefined,
      notPrinted: statusFilter.value === 'notPrinted' ? 1 : undefined,
      notExported: statusFilter.value === 'notExported' ? 1 : undefined,
    };

    const {data} = await api.get('/admin/school-enrollments', {params});

    items.value = data?.items || [];
    total.value = data?.total || 0;
    selectedIds.value = selectedIds.value.filter(id => items.value.some(i => i.id === id));
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Laden fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

function goPage(p) {
  page.value = Math.max(1, Math.min(p, maxPage.value));
  fetchList();
}

function reload() {
  page.value = 1;
  fetchList();
}

function toggleAll(e) {
  if (e.target.checked) {
    selectedIds.value = items.value.map(i => i.id);
  } else {
    selectedIds.value = [];
  }
}

const detailsOpen = ref(false);
const details = ref(null);

async function openDetails(row) {
  await markRead(row.id);
  try {
    const {data} = await api.get(`/admin/school-enrollments/${row.id}`);
    details.value = data;
    detailsOpen.value = true;
    await fetchList(); // Status im Grid aktualisieren
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Details konnten nicht geladen werden.';
  }
}

async function printDetails() {
  if (!details.value) return;
  await markRead(details.value.id);
  await markPrinted(details.value.id);
  exportEnrollmentPdf(details.value);
  await fetchList();
}

async function bulkPdf() {
  if (selectedIds.value.length === 0) return;

  loading.value = true;
  error.value = '';
  try {
    const ids = [...selectedIds.value];

    const doc = new jsPDF();
    let first = true;

    for (const id of ids) {
      await markRead(id);
      await markPrinted(id);

      const {data} = await api.get(`/admin/school-enrollments/${id}`);

      if (!first) doc.addPage();
      first = false;

      exportEnrollmentPdfIntoDoc(doc, data);
    }

    doc.save(`Schulanmeldungen_${ids.length}_Eintraege.pdf`);
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Bulk-PDF fehlgeschlagen.';
  } finally {
    loading.value = false;
    await fetchList();
  }
}

async function bulkCsv() {
  if (selectedIds.value.length === 0) return;

  loading.value = true;
  error.value = '';
  try {
    const ids = [...selectedIds.value];
    const rows = [];

    for (const id of ids) {
      await markRead(id);
      await markExported(id);

      const {data} = await api.get(`/admin/school-enrollments/${id}`);
      rows.push({
        id: data?.id ?? id,
        childFirstName: data?.childFirstName ?? '',
        childLastName: data?.childLastName ?? '',
        schoolYear: data?.schoolYear ?? '',
        classLevel: data?.data?.classLevel ?? '',
        createdAt: data?.createdAt ?? ''
      });
    }

    const header = ['id', 'childFirstName', 'childLastName', 'schoolYear', 'classLevel', 'createdAt'];
    const csv = [
      header.join(';'),
      ...rows.map(r => header.map(k => String(r[k] ?? '').replaceAll(';', ',')).join(';'))
    ].join('\n');

    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Schulanmeldungen_${ids.length}_Eintraege.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Bulk-CSV fehlgeschlagen.';
  } finally {
    loading.value = false;
    await fetchList();
  }
}

// Schulart aus Payload bestimmen (wie im computed schoolTypeLabel)
function getSchoolTypeLabelFrom(payload) {
  const unwrapped = unwrapEnrollmentData(payload);
  const raw = String(unwrapped?.schoolType ?? '').trim();

  if (!raw) return '—';
  if (raw === 'grundschule') return 'Grundschule';
  if (raw === 'sek1') return 'Sekundarstufe I';
  return raw;
}

// Variante: PDF-Export in bestehendes doc (für Bulk)
function exportEnrollmentPdfIntoDoc(doc, payload) {
  // identisch zu exportEnrollmentPdf(), aber ohne doc.save()
  doc.setFontSize(14);
  doc.text('Schulanmeldung für', 14, 16);

  const unwrapped = unwrapEnrollmentData(payload);
  const childFirstName = payload?.childFirstName ?? '';
  const childLastName = payload?.childLastName ?? '';
  const birth = payload?.childBirthDate ?? unwrapped?.childBirthDate ?? '';
  const schoolYear = payload?.schoolYear ?? payload?.data?.schoolYear ?? unwrapped?.schoolYear ?? '';
  const classLevel = payload?.classLevel ?? payload?.data?.classLevel ?? unwrapped?.classLevel ?? '';
  const createdAt = payload?.createdAt ?? '';
  const schoolType = getSchoolTypeLabelFrom(payload);

  const headerBody = [
    ['Vorname', childFirstName || '—'],
    ['Nachname', childLastName || '—'],
    ['Geburtsdatum', formatDate(birth)],
    ['Anmeldung für die', schoolType || '—'],
    ['Schuljahr', schoolYear || '—'],
    ['Klasse', classLevel || '—'],
    ['Anmeldedatum', formatDateTime(createdAt)],
  ];

  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;
  const tableWidth = pageWidth - marginX * 2;
  const halfWidth = tableWidth / 2;

  autoTable(doc, {
    startY: 22,
    body: headerBody,
    styles: {fontSize: 11},
    theme: 'grid',
    tableWidth,
    margin: {left: marginX, right: marginX},
    columnStyles: {
      0: {cellWidth: halfWidth},
      1: {cellWidth: halfWidth},
    },
  });

  let y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || 22;

  const rows = buildDetailsRows(unwrapped);
  if (rows.length > 0) {
    y += 10;
    doc.setFontSize(12);
    doc.text('Weitere Angaben', 14, y);

    const body = rows.map(r => ({
      label: r.label,
      value: r.type === 'section' ? '' : String(r.value ?? '—'),
      _type: r.type,
      _level: r.level
    }));

    autoTable(doc, {
      startY: y + 4,
      body: body.map(r => [r.label, r.value]),
      theme: 'grid',
      tableWidth,
      margin: {left: marginX, right: marginX},
      columnStyles: {
        0: {cellWidth: halfWidth},
        1: {cellWidth: halfWidth},
      },
      styles: {fontSize: 11, cellPadding: {top: 1, right: 4, bottom: 0.3, left: 2}},
      headStyles: {fillColor: [240, 240, 240]},
      didParseCell(data) {
        const raw = body[data.row.index];
        if (!raw) return;
        if (raw._type === 'section') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [248, 248, 248];
          if (data.column.index === 0) {
            data.cell.styles.cellPadding = {top: 1, right: 4, bottom: 0.3, left: 2 + raw._level * 6};
          }
        } else {
          if (data.column.index === 0) {
            data.cell.styles.cellPadding = {top: 1, right: 4, bottom: 0.3, left: 2 + raw._level * 6};
          }
        }
      }
    });
  }
}

// PDF im Detail-Stil mit Sektionen/Einrückungen erzeugen
function exportEnrollmentPdf(payload) {
  const doc = new jsPDF();
  doc.setFontSize(14);
  doc.text('Schulanmeldung für', 14, 16);

  const unwrapped = unwrapEnrollmentData(payload);
  const childFirstName = payload?.childFirstName ?? '';
  const childLastName = payload?.childLastName ?? '';
  const birth = payload?.childBirthDate ?? unwrapped?.childBirthDate ?? '';
  const schoolYear = payload?.schoolYear ?? payload?.data?.schoolYear ?? unwrapped?.schoolYear ?? '';
  const classLevel = payload?.classLevel ?? payload?.data?.classLevel ?? unwrapped?.classLevel ?? '';
  const createdAt = payload?.createdAt ?? '';
  const schoolType = getSchoolTypeLabelFrom(payload);

  const headerBody = [
    ['Vorname', childFirstName || '—'],
    ['Nachname', childLastName || '—'],
    ['Geburtsdatum', formatDate(birth)],
    ['Anmeldung für die', schoolType || '—'],
    ['Schuljahr', schoolYear || '—'],
    ['Klasse', classLevel || '—'],
    ['Anmeldedatum', formatDateTime(createdAt)],
  ];

  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;
  const tableWidth = pageWidth - marginX * 2;
  const halfWidth = tableWidth / 2;

  autoTable(doc, {
    startY: 22,
    body: headerBody,
    styles: {fontSize: 11},
    theme: 'grid',
    tableWidth,
    margin: {left: marginX, right: marginX},
    columnStyles: {
      0: {cellWidth: halfWidth},
      1: {cellWidth: halfWidth},
    },
  });

  let y = (doc.lastAutoTable && doc.lastAutoTable.finalY) || 22;

  // Weitere Angaben im „Details“-Stil (Sektionen + Einrückungen)
  const rows = buildDetailsRows(unwrapped);
  if (rows.length > 0) {
    y += 10;
    doc.setFontSize(12);
    doc.text('Weitere Angaben', 14, y);

    const body = rows.map(r => ({
      label: r.label,
      value: r.type === 'section' ? '' : String(r.value ?? '—'),
      _type: r.type,
      _level: r.level
    }));

    autoTable(doc, {
      startY: y + 4,
      body: body.map(r => [r.label, r.value]),
      theme: 'grid',
      tableWidth,
      margin: {left: marginX, right: marginX},
      columnStyles: {
        0: {cellWidth: halfWidth},
        1: {cellWidth: halfWidth},
      },
      styles: {fontSize: 11, cellPadding: {top: 1, right: 4, bottom: 0.3, left: 2}},
      headStyles: {fillColor: [240, 240, 240]},
      didParseCell(data) {
        const raw = body[data.row.index];
        if (!raw) return;
        if (raw._type === 'section') {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [248, 248, 248];
          if (data.column.index === 0) {
            data.cell.styles.cellPadding = {top: 1, right: 4, bottom: 0.3, left: 2 + raw._level * 6};
          }
        } else {
          if (data.column.index === 0) {
            data.cell.styles.cellPadding = {top: 1, right: 4, bottom: 0.3, left: 2 + raw._level * 6};
          }
        }
      }
    });
  }

  const filenameLast = (childLastName || 'Unbekannt').replace(/\s+/g, '_');
  const filenameFirst = (childFirstName || '').replace(/\s+/g, '_');
  doc.save(`Schulanmeldung_${filenameLast}_${filenameFirst}.pdf`);
}

async function deleteItem(row) {
  if (!confirm(`Eintrag von ${row.childFirstName} ${row.childLastName} löschen?`)) return;
  try {
    await api.delete(`/admin/school-enrollments/${row.id}`);
    await fetchList();
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Löschen fehlgeschlagen.';
  }
}

/**
 * Details: Englisch → Deutsch + einfache Darstellung (kein JSON).
 * Ausgabe als flache Liste mit Einrückungen (level).
 */
const labelMap = {
  childGender: 'Geschlecht',
  nationality: 'Staatsangehörigkeit',
  specialSupportNeeded: 'Besonderer Förderbedarf',
  specialSupportNote: 'Förderbedarf (Hinweis)',
  addressStreet: 'Straße und Haus-Nr.',
  addressZip: 'PLZ',
  addressCity: 'Wohnort',
  currentInstitution: 'Aktuell besuchte Einrichtung',
  notes: 'Besonderheiten / Wünsche',

  mother: 'Mutter',
  father: 'Vater',
  family: 'Familiensituation',
  optional: 'Optional',
  siblings: 'Geschwister (perspektivisch)',

  name: 'Name',
  job: 'Beruf',
  phone: 'Telefon',
  email: 'E‑Mail',

  livingSituation: 'Wir leben',
  childLivesMainlyWith: 'Kind lebt hauptsächlich bei',
  childLivesParity: 'Kind lebt paritätisch bei beiden Elternteilen',
  custody: 'Sorgerecht',
  custodyNote: 'Sorgerecht (Hinweis)',

  schoolYear: 'Schuljahr',
  classLevel: 'Klasse',
  birthDate: 'Geburtstag',

  favoriteSeason: 'Lieblingsjahreszeit',
  likes: 'Das mag ich',
  dislikes: 'Das mag ich nicht so gerne',
  proudOf: 'Darauf bin ich stolz',
  dreamSchoolDescription: 'Wenn ich eine Schule bauen könnte …',
};

function humanizeKey(k) {
  const key = String(k ?? '');
  if (labelMap[key]) return labelMap[key];
  // fallback: camelCase → Wörter
  return key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/^./, (c) => c.toUpperCase());
}

function formatValue(key, value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';

  if (key === 'childGender') {
    const v = String(value).toLowerCase();
    if (v === 'weiblich') return 'weiblich';
    if (v === 'maennlich' || v === 'männlich') return 'männlich';
    if (v === 'unbestimmt') return 'unbestimmt';
  }

  if (key === 'livingSituation') {
    const v = String(value).toLowerCase();
    if (v === 'zusammen') return 'zusammen mit dem Kind';
    if (v === 'getrennt') return 'getrennt';
  }

  if (key === 'custody') {
    const v = String(value).toLowerCase();
    if (v === 'gemeinsam') return 'gemeinsam';
    if (v === 'bei') return 'liegt bei (siehe Hinweis)';
    if (v === 'unbekannt') return 'unbekannt';
  }

  if (Array.isArray(value)) {
    // Für Arrays (z. B. Geschwister): kompakt, pro Element später mit Unterpunkten
    return value.length === 0 ? '—' : `${value.length} Eintrag${value.length === 1 ? '' : 'e'}`;
  }

  if (typeof value === 'object') return '—';
  return String(value);
}

function pushRow(rows, level, label, value) {
  rows.push({type: 'row', level, label, value});
}

function pushSection(rows, level, label) {
  rows.push({type: 'section', level, label});
}

/**
 * API liefert teils data oder data.data (doppelt verschachtelt).
 * Diese Funktion gibt immer das „eigentliche“ Formular-Objekt zurück.
 */
function unwrapEnrollmentData(payload) {
  const root = payload && typeof payload === 'object' ? payload : null;
  if (!root) return {};

  const d1 = root.data;
  if (!d1 || typeof d1 !== 'object') return {};

  // Fall: { data: { ...formFields... , classLevel? } }
  const looksLikeForm =
      'childGender' in d1 ||
      'mother' in d1 ||
      'father' in d1 ||
      'family' in d1 ||
      'optional' in d1 ||
      'addressCity' in d1 ||
      'addressStreet' in d1 ||
      'addressZip' in d1 ||
      'notes' in d1;

  if (looksLikeForm) return d1;

  // Fall: { data: { data: { ...formFields... }, classLevel: '...' } }
  const d2 = d1.data;
  if (d2 && typeof d2 === 'object') return d2;

  return {};
}

function buildDetailsRows(data) {
  const rows = [];
  if (!data || typeof data !== 'object') return rows;

  // 1) Einfache Felder oben
  const simpleKeys = [
    'childGender',
    'nationality',
    'specialSupportNeeded',
    'specialSupportNote',
    'addressStreet',
    'addressZip',
    'addressCity',
    'currentInstitution',
    'notes',
  ];

  for (const k of simpleKeys) {
    if (!(k in data)) continue;
    const v = data[k];
    if (k === 'specialSupportNote' && !String(v ?? '').trim()) continue;
    pushRow(rows, 0, humanizeKey(k), formatValue(k, v));
  }

  // 2) Eltern
  const parentBlocks = [
    {key: 'mother', label: humanizeKey('mother')},
    {key: 'father', label: humanizeKey('father')},
  ];

  for (const p of parentBlocks) {
    const obj = data[p.key];
    if (!obj || typeof obj !== 'object') continue;

    const hasAny = ['name', 'job', 'phone', 'email'].some((kk) => String(obj?.[kk] ?? '').trim());
    if (!hasAny) continue;

    pushSection(rows, 0, p.label);
    for (const kk of ['name', 'job', 'phone', 'email']) {
      if (!String(obj?.[kk] ?? '').trim()) continue;
      pushRow(rows, 1, humanizeKey(kk), formatValue(kk, obj[kk]));
    }
  }

  // 3) Familie
  if (data.family && typeof data.family === 'object') {
    const f = data.family;
    const hasAny =
        String(f.livingSituation ?? '').trim() ||
        String(f.childLivesMainlyWith ?? '').trim() ||
        f.childLivesParity === true ||
        String(f.custody ?? '').trim() ||
        String(f.custodyNote ?? '').trim();

    if (hasAny) {
      pushSection(rows, 0, humanizeKey('family'));
      if (String(f.livingSituation ?? '').trim()) pushRow(rows, 1, humanizeKey('livingSituation'), formatValue('livingSituation', f.livingSituation));
      if (String(f.childLivesMainlyWith ?? '').trim()) pushRow(rows, 1, humanizeKey('childLivesMainlyWith'), formatValue('childLivesMainlyWith', f.childLivesMainlyWith));
      if (f.childLivesParity === true) pushRow(rows, 1, humanizeKey('childLivesParity'), 'Ja');
      if (String(f.custody ?? '').trim()) pushRow(rows, 1, humanizeKey('custody'), formatValue('custody', f.custody));
      if (String(f.custodyNote ?? '').trim()) pushRow(rows, 1, humanizeKey('custodyNote'), formatValue('custodyNote', f.custodyNote));
    }
  }

  // 4) Geschwister (Array)
  if (Array.isArray(data.siblings) && data.siblings.length > 0) {
    const siblings = data.siblings
        .filter((s) => s && typeof s === 'object')
        .filter((s) =>
            String(s.name ?? '').trim() ||
            String(s.schoolYear ?? '').trim() ||
            String(s.classLevel ?? '').trim() ||
            String(s.birthDate ?? '').trim()
        );

    if (siblings.length > 0) {
      pushSection(rows, 0, humanizeKey('siblings'));
      siblings.forEach((s, idx) => {
        pushSection(rows, 1, `Geschwisterkind ${idx + 1}`);
        if (String(s.name ?? '').trim()) pushRow(rows, 2, humanizeKey('name'), formatValue('name', s.name));
        if (String(s.birthDate ?? '').trim()) pushRow(rows, 2, humanizeKey('birthDate'), formatDate(s.birthDate));
        if (String(s.schoolYear ?? '').trim()) pushRow(rows, 2, humanizeKey('schoolYear'), formatValue('schoolYear', s.schoolYear));
        if (String(s.classLevel ?? '').trim()) pushRow(rows, 2, humanizeKey('classLevel'), formatValue('classLevel', s.classLevel));
      });
    }
  }

  // 5) Optional
  if (data.optional && typeof data.optional === 'object') {
    const o = data.optional;
    const optKeys = ['favoriteSeason', 'likes', 'dislikes', 'proudOf', 'dreamSchoolDescription'];
    const hasAny = optKeys.some((k) => String(o?.[k] ?? '').trim());
    if (hasAny) {
      pushSection(rows, 0, humanizeKey('optional'));
      for (const k of optKeys) {
        if (!String(o?.[k] ?? '').trim()) continue;
        pushRow(rows, 1, humanizeKey(k), formatValue(k, o[k]));
      }
    }
  }

  return rows;
}

const detailsRows = computed(() => {
  const unwrapped = unwrapEnrollmentData(details.value);
  return buildDetailsRows(unwrapped);
});

onMounted(async () => {
  await fetchFacetOptions();
  await fetchList();
});
</script>

<style scoped>
.school-enrollments-page {
  background: var(--bg);
  color: var(--text);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--border);
}

.actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.h1 {
  font-weight: 600;
  margin: 0;
}

.h2 {
  font-size: 22px;
  line-height: 22px;
  font-weight: 600;
  margin: 0;
}

.h3 {
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.kv-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 6px;
}

.kv-item {
  padding: 0 10px;
  font-size: 14px;
  line-height: 14px;
}

.kv-section {
  background: var(--surface);
}

.kv-label {
  display: inline-block;
  width: 230px;
  font-weight: 500;
}

.kv-value {
  color: var(--text);
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
}

.filters {
  display: grid;
  grid-template-columns: 1fr 160px 100px 180px auto;
  gap: 12px;
  align-items: end;
  font-size: 14px;
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.row-unread td {
  font-weight: 600;
}

.status-icons {
  white-space: nowrap;
}

.status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 18px;
  border-radius: 6px;
  border: 1px solid rgba(16, 185, 129, .5);
  color: var(--success);
  background: rgba(16, 185, 129, .08);
  margin-left: 24px;
}

td.white-space-nowrap {
  position: relative;
  width: 70px;
}

td.white-space-nowrap input {
  position: absolute;
  top: calc(100% / 2 - 9px);
}

.status-on {
  color: var(--success);
}

.status-off {
  color: var(--muted);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin-bottom: 0;
}

.filters-actions {
  display: inline-flex;
  gap: 6px;
  flex-direction: row;
}

.input {
  min-height: 40px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
}

.btn {
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
}

.btn-primary {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

.btn-danger {
  color: var(--danger);
  border-color: color-mix(in srgb, var(--danger) 30%, var(--border));
}

.notice-danger {
  border: 1px solid rgba(239, 68, 68, 0.35);
  background: rgba(239, 68, 68, 0.08);
  border-radius: 6px;
  padding: 10px 12px;
}

.table-wrap {
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.table th,
.table td {
  border-bottom: 1px solid var(--border);
  padding: 4px 10px;
  text-align: left;
  vertical-align: middle;
}

.table tr:last-child td {
  border-bottom: 0;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Pagination-Aufteilung wie Groups.vue */
.mt-12 {
  margin-top: 12px;
}

.pagination-row {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.muted {
  color: var(--muted);
}

.details-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: grid;
  place-items: center;
  z-index: 1000;
}

.details-card {
  width: min(800px, 96vw);
  max-height: 90vh;
  overflow: auto;
}

.payload {
  background: color-mix(in srgb, var(--primary) 2%, transparent);
  border: 1px solid var(--border);
  padding: 10px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.35;
}

.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}

@media (max-width: 768px) {
  .filters {
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .bulk-actions {
    flex-wrap: wrap;
  }

  .filters-actions,
  .row-actions {
    width: 100%;
  }

  .filters-actions .btn,
  .row-actions .btn {
    width: 100%;
  }

  .grid2 {
    grid-template-columns: 1fr;
  }

  .kv-label {
    min-width: 0;
    display: block;
    margin-bottom: 2px;
  }
}
</style>